import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  getClientsListByBillingClientIdQuery,
  createClientByBillingClientIdQuery,
  editClientByBillingClientIdQuery,
  deleteClientByBillingClientIdQuery,
  getHistoryBillingClientByClientIdQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import { QUERY_STATUS } from "constants/constants";

const BillingClientDatatrackContext = createContext({});

export const useBillingClientDatatrackContext = () =>
  useContext(BillingClientDatatrackContext);

export const BillingClientDatatrackProvider = ({ ...props }) => {
  const [clientsListByBillingClientId, setClientsListByBillingClientId] =
    useState([]);
  const [historyBillingClientByClientId, setHistoryBillingClientByClientId] =
    useState([]);
  const [refresh, setRefresh] = useState(false);
  const [billingClientStatus, setBillingClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const { userScope } = useUserContext();

  const getClientsListByBillingClientId = useCallback(async ({ id, scope }) => {
    try {
      const result = await getClientsListByBillingClientIdQuery({
        id,
        scope,
      });
      setClientsListByBillingClientId(result);
    } catch (e) {
      console.error("getClientsListByBillingClientId", e);
      throw e;
    }
  }, []);

  const getHistoryBillingClientByClientId = useCallback(
    async ({ id, scope }) => {
      try {
        const result = await getHistoryBillingClientByClientIdQuery({
          id,
          scope,
        });
        setHistoryBillingClientByClientId(result);
      } catch (e) {
        console.error("getHistoryBillingClientByClientId", e);
        throw e;
      }
    },
    []
  );
  const createClientByBillingClientId = useCallback(async ({ data, scope }) => {
    try {
      setBillingClientStatus(QUERY_STATUS.LOADING);
      const result = await createClientByBillingClientIdQuery({
        data,
        scope,
      });
      setHistoryBillingClientByClientId([
        ...historyBillingClientByClientId,
        result,
      ]);
      setBillingClientStatus(QUERY_STATUS.SUCCESS);

      setRefresh(true);
    } catch (e) {
      setBillingClientStatus(QUERY_STATUS.ERROR);
      console.error("createClientByBillingClientId", e);
      throw e;
    }
  }, []);

  const editClientByBillingClientId = useCallback(
    async ({ id, data, scope }) => {
      try {
        setBillingClientStatus(QUERY_STATUS.LOADING);
        const result = await editClientByBillingClientIdQuery({
          id,
          data,
          scope,
        });
        setHistoryBillingClientByClientId((old) =>
          old.map((elm) => (elm?.id === id ? result : elm))
        );
        setBillingClientStatus(QUERY_STATUS.SUCCESS);
        setRefresh(true);
      } catch (e) {
        setBillingClientStatus(QUERY_STATUS.ERROR);
        console.error("editClientByBillingClientIdQuery", e);
        throw e;
      }
    },
    []
  );

  const deleteClientByBillingClientId = useCallback(
    async ({ id, scope }) => {
      try {
        setBillingClientStatus(QUERY_STATUS.LOADING);
        await deleteClientByBillingClientIdQuery({ id, scope });
        setHistoryBillingClientByClientId((old) =>
          old.filter((elm) => elm.id !== id)
        );
        setBillingClientStatus(QUERY_STATUS.SUCCESS);
        setRefresh(true);
      } catch (e) {
        setBillingClientStatus(QUERY_STATUS.ERROR);
        console.error("deleteClientByBillingClientIdQuery", e);
        throw e;
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      clientsListByBillingClientId,
      setClientsListByBillingClientId,
      getClientsListByBillingClientId,
      createClientByBillingClientId,
      editClientByBillingClientId,
      deleteClientByBillingClientId,
      getHistoryBillingClientByClientId,
      historyBillingClientByClientId,
      setRefresh,
      refresh,
      billingClientStatus,
      setBillingClientStatus,
    }),
    [
      clientsListByBillingClientId,
      historyBillingClientByClientId,
      refresh,
      billingClientStatus,
    ]
  );

  return <BillingClientDatatrackContext.Provider value={values} {...props} />;
};
